import { Spinner } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@telegram-apps/sdk';
import { useSettings } from '../hooks';
import { INTERVAL_OPTIONS, QUIET_HOURS_PRESETS } from '../types';
import './SettingsPage.css';

// Toggle component
function Toggle({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  const handleClick = () => {
    if (disabled) return;
    if (hapticFeedback.impactOccurred.isAvailable()) {
      hapticFeedback.impactOccurred('light');
    }
    onChange(!checked);
  };

  return (
    <div
      className={`settings-toggle ${checked ? 'settings-toggle--on' : ''} ${disabled ? 'settings-toggle--disabled' : ''}`}
      onClick={handleClick}
    >
      <div className="settings-toggle-thumb" />
    </div>
  );
}

// Settings row component
function SettingsRow({
  label,
  subtitle,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  subtitle?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="settings-row">
      <div className="settings-row-content">
        <span className="settings-row-label">{label}</span>
        {subtitle && <span className="settings-row-subtitle">{subtitle}</span>}
      </div>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

// Interval selector component
function IntervalSelector({
  value,
  onChange,
  disabled = false,
}: {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}) {
  const handleClick = (optionValue: number) => {
    if (disabled) return;
    if (hapticFeedback.selectionChanged.isAvailable()) {
      hapticFeedback.selectionChanged();
    }
    onChange(optionValue);
  };

  return (
    <div className="settings-interval">
      {INTERVAL_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`settings-interval-btn ${value === option.value ? 'settings-interval-btn--active' : ''}`}
          onClick={() => handleClick(option.value)}
          disabled={disabled}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// Quiet hours selector component
function QuietHoursSelector({
  startValue,
  endValue,
  onChange,
  disabled = false,
}: {
  startValue: number;
  endValue: number;
  onChange: (start: number, end: number) => void;
  disabled?: boolean;
}) {
  const handleClick = (start: number, end: number) => {
    if (disabled) return;
    if (hapticFeedback.selectionChanged.isAvailable()) {
      hapticFeedback.selectionChanged();
    }
    onChange(start, end);
  };

  const isActive = (start: number, end: number) =>
    startValue === start && endValue === end;

  return (
    <div className="settings-quiet-hours">
      {QUIET_HOURS_PRESETS.map((preset) => (
        <button
          key={preset.label}
          type="button"
          className={`settings-quiet-hours-btn ${isActive(preset.start, preset.end) ? 'settings-quiet-hours-btn--active' : ''}`}
          onClick={() => handleClick(preset.start, preset.end)}
          disabled={disabled}
        >
          <span className="settings-quiet-hours-icon">🌙</span>
          <span>{preset.label}</span>
        </button>
      ))}
    </div>
  );
}

export function SettingsPage() {
  const { settings, isLoading, error, updateSettings, isUpdating } = useSettings();

  if (isLoading) {
    return (
      <div className="settings-loading">
        <Spinner size="l" />
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="settings-error">
        <div className="settings-error-icon">⚠</div>
        <h2 className="settings-error-title">Ошибка загрузки</h2>
        <p className="settings-error-text">Не удалось загрузить настройки</p>
      </div>
    );
  }

  const handleToggle = (key: keyof typeof settings) => (value: boolean) => {
    updateSettings({ [key]: value });
  };

  const handleIntervalChange = (value: number) => {
    updateSettings({ reminderInterval: value });
  };

  const handleQuietHoursChange = (start: number, end: number) => {
    updateSettings({
      quietHoursStart: start,
      quietHoursEnd: end,
    });
  };

  return (
    <div className="settings-page">
      {/* Header */}
      <header className="settings-header">
        <h1 className="settings-title">Настройки</h1>
      </header>

      {/* Main notification toggle */}
      <div className="settings-section">
        <div className="settings-section-header">Уведомления</div>
        <div className="settings-card">
          <SettingsRow
            label="Получать уведомления"
            subtitle="Уведомления о новых и изменённых заявках"
            checked={settings.receiveNotifications}
            onChange={handleToggle('receiveNotifications')}
            disabled={isUpdating}
          />
        </div>
      </div>

      {/* Reminder interval */}
      {settings.receiveNotifications && (
        <div className="settings-section animate-fade-in">
          <div className="settings-section-header">Интервал напоминаний</div>
          <p className="settings-section-subtitle">
            Как часто напоминать о заявках, требующих реакции
          </p>
          <div className="settings-card settings-card--flat">
            <IntervalSelector
              value={settings.reminderInterval}
              onChange={handleIntervalChange}
              disabled={isUpdating}
            />
          </div>
        </div>
      )}

      {/* Quiet hours */}
      {settings.receiveNotifications && (
        <div className="settings-section animate-fade-in">
          <div className="settings-section-header">Тихие часы</div>
          <div className="settings-card">
            <SettingsRow
              label="Включить тихие часы"
              subtitle="Не отправлять уведомления в указанное время"
              checked={settings.quietHoursEnabled}
              onChange={handleToggle('quietHoursEnabled')}
              disabled={isUpdating}
            />
          </div>
          {settings.quietHoursEnabled && (
            <div className="settings-card settings-card--flat animate-fade-in" style={{ marginTop: '8px' }}>
              <QuietHoursSelector
                startValue={settings.quietHoursStart}
                endValue={settings.quietHoursEnd}
                onChange={handleQuietHoursChange}
                disabled={isUpdating}
              />
            </div>
          )}
        </div>
      )}

      {/* Notification types */}
      {settings.receiveNotifications && (
        <div className="settings-section animate-fade-in">
          <div className="settings-section-header">Типы уведомлений</div>
          <p className="settings-section-subtitle">
            О каких заявках получать уведомления
          </p>
          <div className="settings-card">
            <SettingsRow
              label="Требуют реакции"
              subtitle="Заявки со статусом «Назначено»"
              checked={settings.notifyNeedsResponse}
              onChange={handleToggle('notifyNeedsResponse')}
              disabled={isUpdating}
            />
            <SettingsRow
              label="В работе"
              subtitle="Заявки со статусом «В работе»"
              checked={settings.notifyInProgress}
              onChange={handleToggle('notifyInProgress')}
              disabled={isUpdating}
            />
            <SettingsRow
              label="Ожидание ответа заказчика"
              subtitle="Заявки, ожидающие ответа"
              checked={settings.notifyWaitingCustomer}
              onChange={handleToggle('notifyWaitingCustomer')}
              disabled={isUpdating}
            />
          </div>
        </div>
      )}
    </div>
  );
}
